import {
	BackdropCircularProgress,
	DeleteConfirmationDialog,
	EnhancedDialog,
	EnhancedSnackbar,
	IDNDList,
	IResponse,
	IServerError,
	SingleColumnLayout,
	StandardPanel,
} from "@websential/cosmic-v20";
import { FormProvider, useForm } from "react-hook-form";
import { Grid, useTheme } from "@mui/material";
import {
	ICompanyStatutoryHoliday,
	ICompanyStatutoryHolidayForm,
} from "../../models/companyStatutoryHoliday";
import {
	INITIAL_PAGE_SIZE,
	INITIAL_SNACKBAR_DATA,
	QUERY_OPTIONS,
	ROWS_PER_PAGE_PER_OPTIONS,
} from "../../common/constants";
import { Link, useNavigate, useParams } from "react-router-dom";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
	formatDate,
	formatDateToShow,
	scrollPanelFieldsToTop,
	sortDDLByLabel,
	sortDragDropContextDDLByText,
	useNavigateToLogout,
} from "../../common/Common";
import {
	provinceCardsData,
	provinceMap,
} from "./Panels/StatutoryHolidayConstantData";
import { useDispatch, useSelector } from "react-redux";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { CompanyStatutoryHolidayInfoSetupFieldsPanel } from "./Panels/CompanyStatutoryHolidaySetupInfoSetupFieldsPanel";
import { CompanyStatutoryHolidayService } from "../../services/CompanyStatutoryHolidayService";
import { CompanyStatutoryHolidaySetupToolbar } from "./CompanyStatutoryHolidaySetupToolbar";
import { CountryService } from "../../services/common/CountryService";
import { DDLDataTableStatutoryHoliday } from "./Panels/DDLDataTable/DDLDataTableCompanyStatutoryHoliday";
import { EmployeeService } from "../../services/EmployeeService";
import { EnhancedButtonPrimeReact } from "../EnhancedButtonPrimeReact";
import { IAppState } from "../../store/reducers";
import { IGetListRequest } from "../../models";
import { IMoveItemsPayload } from "../DragDropDDLPanel";
import { StatutoryHolidayEligibilityPanel } from "./Panels/StatutoryHolidayEligibilityPanel";
import { StatutoryHolidayService } from "../../services/StatutoryHolidayService";
import { StatutoryHolidayProfileListStatusDataTable } from "./Panels/StatutoryHolidayStatusDataTable/StatutoryHolidayStatusDataTable";
import { StatutoryHolidaysProvinceCards } from "./Panels/StatutoryHolidaysProvinceCards";
import { instanceOfICompanyStatutoryHolidayScreenPanelAssignmentResponsePayload } from "../../common/instance-method";
import { setSelectedDrawerOptionsMenu } from "../../store/actions/drawerMenuOptionsActions";
import { setSelectedStickyNavBar } from "../../store/actions";

export interface ICompanyStatutoryHolidaySetup { }
const STATORY_HOLIDAY_RECORD_CARDS_DATA_QUERY_KEY = "statutory-holiday-cards-data-record";
const STATORY_HOLIDAY_RECORD_FORM_DATA_QUERY_KEY = "statutory-holiday-form-data-record";
const STATORY_HOLIDAY_RECORD_ELIGIBILITY_DATA_QUERY_KEY = "statutory-holiday-eligibilty-data-record";
const STATUTORY_HOLIDAY_PROFILE_LIST_STATUS = "statutory-holiday-profile-list-status";

export const firstPanelDraggableId = "firstList";
export const secondPanelDraggableId = "secondList";

/// main form 
const useGetDataStatutoryHolidayForm = (
	clientId: string | undefined,
	navigateToLogout: (path: string) => void
) =>
	useQuery({
		queryKey: [STATORY_HOLIDAY_RECORD_FORM_DATA_QUERY_KEY, clientId, navigateToLogout],
		queryFn: () => {
			return CompanyStatutoryHolidayService.getStatutoryHolidayFormDataById(clientId, navigateToLogout);
		},
		refetchOnWindowFocus: false,
	});

// province cards list 
const useGetDataStatutoryHolidayProvinceCardsList = (
	clientId: string | undefined,
	params: IGetListRequest,
	navigateToLogout: (path: string) => void
) =>
	useQuery({
		queryKey: [STATORY_HOLIDAY_RECORD_CARDS_DATA_QUERY_KEY, clientId, navigateToLogout],
		queryFn: (data) => {

			return CompanyStatutoryHolidayService.getStatutoryHolidayProvinceCardsListById(clientId, params, navigateToLogout);
		},
		refetchOnWindowFocus: false,
	});


const useGetAssignedEligiblePanels = (
	clientId: string,
) =>
	useQuery({
		queryKey: [STATORY_HOLIDAY_RECORD_ELIGIBILITY_DATA_QUERY_KEY, clientId],
		queryFn: (data) => {

			return CompanyStatutoryHolidayService.getStatutoryHolidayEemployeePanelsAssignedToScreen(clientId);
		},
		refetchOnWindowFocus: false,
		enabled: false
	});

const useGetEmployeeStatusDDL = (clientId: string) =>
	useQuery({
		queryKey: ["employee-status-ddl", clientId],
		queryFn: () => {
			return EmployeeService.getGetEmployeeStatusDDL(clientId);
		},
		refetchOnWindowFocus: false,
	});

const useStatutoryHolidayDDL = (provinceId: string | null) =>
	useQuery({
		queryKey: ["statutory-holiday-ddl", provinceId],
		queryFn: (data) => {
			return StatutoryHolidayService.getStatutoryHolidayDDL(provinceId);
		},
		refetchOnWindowFocus: false,
	});

const useGetStatutoryHolidayProfileListStatus = (
	clientId: string,
	taxYear: number,
	navigateToLogout: (path: any) => void
) =>
	useQuery({
		queryKey: [
			STATUTORY_HOLIDAY_PROFILE_LIST_STATUS,
			clientId,
			taxYear,
			navigateToLogout
		],
		queryFn: () =>
			CompanyStatutoryHolidayService.getStatutoryHolidayProfileListStatus(
				clientId,
				taxYear,
				navigateToLogout
			),
		refetchOnWindowFocus: false,
	});

export const StatutoryHolidayProfileListheaderColumns = [
	{
		field: `provinceShortCode`,
		headerName: `Holiday`,
		description: "",
		flex: 1,
		hide: false,
		hideable: false,
		sortable: true,
		sortingOrder: "asc",
		resizable: false,
		type: "string",
		align: "left",
		headerAlign: "center",
		hideSortIcons: false,
		disableColumnMenu: true,
		disableReorder: true,
		disableExport: true,
		maskable: false,
		isFixed: true,
		sequence: 0,
		frozenColumn: false,
		footer: null,
	},
	{
		field: "holidayDate",
		headerName: "Date",
		description: "",
		flex: 1,
		hide: false,
		hideable: false,
		sortable: true,
		sortingOrder: "asc",
		resizable: false,
		type: "string",
		align: "left",
		headerAlign: "center",
		minWidth: "12rem",
		hideSortIcons: false,
		disableColumnMenu: true,
		disableReorder: true,
		disableExport: true,
		maskable: false,
		isFixed: true,
		sequence: 1,
		frozenColumn: false,
		footer: null,
	},
	{
		field: "status",
		headerName: "Status",
		description: "",
		flex: 1,
		hide: false,
		hideable: false,
		sortable: true,
		sortingOrder: "asc",
		resizable: false,
		type: "string",
		align: "left",
		headerAlign: "center",
		hideSortIcons: false,
		disableColumnMenu: true,
		disableReorder: true,
		disableExport: true,
		maskable: false,
		isFixed: true,
		sequence: 3,
		frozenColumn: false,
		footer: null,
		minWidth: 150,
		maxWidth: 150,
	},
];

export const CompanyStatutoryHolidaySetup: React.FC<
	ICompanyStatutoryHolidaySetup
> = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const drawerMenuOptions = useSelector(
		(state: IAppState) => state.drawerMenu.menu
	);
	const theme = useTheme();
	const subMenuVisibility = useSelector(
		(state: IAppState) => state.drawerAction.subMenuVisibility
	);
	// const canadaProvinces = useSelector((state: IAppState) => state.countryAndProvincesInfo.canadaProvinces)
	const countryList = useSelector((state: IAppState) => state.countryAndProvincesInfo.countryDDL);
	const navigateToLogout = useNavigateToLogout();
	const [openDialog, setDialogOpen] = useState(false);
	const [openArchiveDialog, setArchiveDialogOpen] = useState(false);
	const [singleDelete, setSingleDelete] = useState<boolean>(false);
	const [singleArchive, setSingleArchive] = useState<boolean>(false);
	const [statutoryHolidayId, setStatutoryHolidayId] = useState<number | string>(
		0
	);
	const [snackbarData, setSnackbarData] = useState(INITIAL_SNACKBAR_DATA);
	const [provinceId, setProvinceId] = useState<null | string>(null);
	const [provinceSC, setProvinceSC] = useState<null | string>(null);
	const [statutoryHolidayDDLForAllProvinces, setStatutoryHolidayDDLForAllProvinces] = useState<any>(
		{
			AB: [],
			BC: [],
			MB: [],
			NT: [],
			NB: [],
			NS: [],
			NU: [],
			NL: [],
			PE: [],
			ON: [],
			QC: [],
			SK: [],
			YT: [],
		}
	);
	const [provinceDDLItems, setProvinceDDLItems] = useState<any>([]);
	const [provinceDDL, setProvinceDDL] =
		useState<any>(undefined);
	const [statutoryHolidaysProfileListStatusUpdatedData, setStatutoryHolidaysProfileListStatusUpdatedData] = useState(
		undefined
	);
	console.log("statutoryHolidaysProfileListStatusUpdatedData", statutoryHolidaysProfileListStatusUpdatedData)
	const queryClient = useQueryClient();
	const { id } = useParams();

	const [editRecordId, setEditRecordId] = useState(id);

	const clientId = useSelector((state: IAppState) => state.client.id);
	const taxYear = useSelector((state: IAppState) => state.taxYear.year);

	const [disableCards, setDisableCards] = useState<any>(false);

	const [itemSearch, setItemSearch] = useState("");


	const [firstList, setFirstList] = useState<IDNDList>({
		droppableId: "firstList",
		elements: [],
		testId: "firstList",
	});

	const [secondList, setSecondList] = useState<IDNDList>({
		droppableId: "secondList",
		elements: [],
		testId: "secondList",
	});
	// const [selectedIds, setSelectedIds] = useState<string[]>([]);
	// const [selectedListId, setSelectedListId] = useState<string | null>(null);
	// const lastSelectedIndexRef = useRef<number | null>(null);


	const [openEligibilityPanel, setOpenEligibilityPanel] = useState(false);

	const [queryParams, setQueryParams] = useState<IGetListRequest>({
		search_query: "",
		order_by: "effectiveDate",
		order_direction: "asc",
		page: 1,
		size: 25,
	});

	const onRequestParamsChange = useCallback((requestParams: any) => {
		let dispatchParams = {
			search_query: requestParams.searchQuery ? requestParams.searchQuery : "",
			order_by:
				requestParams.sortField !== null
					? requestParams.sortField
					: "effectiveDate",
			order_direction: requestParams.sortOrder
				? requestParams.sortOrder
				: "asc",
			page: requestParams.page + 1,
			size: requestParams.rows,
		};

		setQueryParams(dispatchParams);
	}, []);

	const onSnackbarClosed = (
		_event: React.SyntheticEvent | Event,
		reason?: string
	) => {
		if (reason === "clickaway") {
			return;
		}
		setSnackbarData(INITIAL_SNACKBAR_DATA);
	};

	const DEFAULT_VALUES = {
		CalculateStatutoryHoliday: 100,
		AccruePayout: 100,
		DisplayStatutoryHoliday: 200,
	};

	const formMethods = useForm<any>({
		mode: "onBlur",
		reValidateMode: "onChange",
		defaultValues: DEFAULT_VALUES
	});

	useEffect(() => {
		if (countryList && Array.isArray(countryList)) {
			const canada = countryList.find(
				(item: any) => item.label === "Canada" || "canada"
			);
			if (canada) {
				(async () => {
					try {
						const response = await CountryService.getProvinceOrStateDDL(
							navigateToLogout,
							canada.value,
							"Province"
						);
						setProvinceDDL(response);
					} catch (err) {
						console.error("error", err);
					}
				})();
			} else {
				setProvinceDDL(undefined);
			}
		}
	}, [countryList]);
	const { isLoading: isLoadingStatutoryHolidayDDL, data: statutoryHolidayDDL } = useStatutoryHolidayDDL(provinceId);

	const provinceSCRef = useRef<any>(provinceSC);
	useEffect(() => {
		provinceSCRef.current = provinceSC;
	}, [provinceSC]);
	useEffect(() => {
		if (provinceSCRef.current && statutoryHolidayDDL) {
			setStatutoryHolidayDDLForAllProvinces((prev: any) => ({
				...prev,
				[provinceSCRef.current]: statutoryHolidayDDL,
			}));
		}
	}, [statutoryHolidayDDL]);

	const { data: useDataProvinceCardsList } = useGetDataStatutoryHolidayProvinceCardsList(
		clientId,
		queryParams,
		navigateToLogout
	);

	const getProvinceCardsList: any | null = useDataProvinceCardsList;

	const { data: employeeStatusDDl } = useGetEmployeeStatusDDL(clientId)



	const {
		isLoading: isLoadingAssignedClients,
		data: assignedEligiblePanelResponsePayload,
		refetch: refetchAssignedEligiblePanels,
	} = useGetAssignedEligiblePanels(clientId);

	const {
		isPending: isSubmitting,
		mutate: submitCompanyStatutoryHolidayFormFields,
	} = useMutation({
		mutationFn: CompanyStatutoryHolidayService.create,
		onSuccess: (
			res: ICompanyStatutoryHolidayForm | IServerError | IResponse | any
		) => {

			if (res.status === 200) {
				setSnackbarData({
					open: true,
					message: "Success",
					severity: "success",
				});
				scrollPanelFieldsToTop();
				let path = `/settings/statutory-holiday/`;
				navigate(path);
				return queryClient.invalidateQueries({ queryKey: [STATORY_HOLIDAY_RECORD_FORM_DATA_QUERY_KEY] });
			} else if (res.status === 201) {
				setSnackbarData({
					open: true,
					message: "Success",
					severity: "success",
				});
				scrollPanelFieldsToTop();
				let path = `/settings/statutory-holiday/`;
				navigate(path);
				return queryClient.invalidateQueries({ queryKey: [STATORY_HOLIDAY_RECORD_FORM_DATA_QUERY_KEY] });
			} else {
				setSnackbarData({
					open: true,
					message: res.message ? res.message : "Something went wrong!",
					severity: "error",
				});
			}
		},
		onError: () => {
			setSnackbarData({
				open: true,
				message: "Something went wrong, try again later!",
				severity: "error",
			});
		},
	});

	const {
		isPending: submitCompanyStatutoryHolidayProvinceCardField,
		mutate: submitCompanyStatutoryHolidayProvinceCardFields,
	} = useMutation({
		mutationFn:
			CompanyStatutoryHolidayService.createStatutoryHolidayProvinceCards,
		onSuccess: (
			res: ICompanyStatutoryHoliday | IServerError | IResponse | any
		) => {

			if (res.status === 200) {
				setSnackbarData({
					open: true,
					message: "Success",
					severity: "success",
				});
				scrollPanelFieldsToTop();
				// let path = `/settings/statutory-holiday/`;
				// navigate(path);
				// return queryClient.invalidateQueries({
				// 	queryKey: [STATORY_HOLIDAY_RECORD_CARDS_DATA_QUERY_KEY],
				// });
			} else if (res.status === 201) {
				setSnackbarData({
					open: true,
					message: "Success",
					severity: "success",
				});
				scrollPanelFieldsToTop();
				let path = `/settings/statutory-holiday/`;
				navigate(path);
				return queryClient.invalidateQueries({
					queryKey: [STATORY_HOLIDAY_RECORD_CARDS_DATA_QUERY_KEY],
				});
			} else {
				setSnackbarData({
					open: true,
					message: res.message ? res.message : "Something went wrong!",
					severity: "error",
				});
			}
		},
		onError: () => {
			setSnackbarData({
				open: true,
				message: "Something went wrong, try again later!",
				severity: "error",
			});
		},
	});



	const { isPending: isPendingStatutoryHolidayFormDataByClientId, data: statutoryHolidayFormDataByClientId } = useGetDataStatutoryHolidayForm(
		clientId,
		navigateToLogout
	);



	const onSubmit = (formValues: any) => {

		let payload = {
			clientId: clientId && clientId,
			// id: "68e7a274b2e0e3f58afda6d6",
			id: id && id,
			taxYear: taxYear && taxYear,
			navigateToLogout: navigateToLogout,
			calculateStatutoryHoliday: formValues.calculateStatutoryHoliday,
			accruePayout: formValues.accruePayout,
			displayStatutoryHoliday: formValues.displayStatutoryHoliday,
		};
		submitCompanyStatutoryHolidayFormFields(payload);
	};

	useEffect(() => {
		if (clientId && statutoryHolidayFormDataByClientId) {

			Object.keys(statutoryHolidayFormDataByClientId).forEach((fieldName) => {
				const fieldValue = statutoryHolidayFormDataByClientId[fieldName];
				formMethods.setValue(fieldName, fieldValue ?? undefined);
				if (fieldName === "id") {
					let path = `/settings/statutory-holiday/${fieldValue}`
					navigate(path)
				}
			});
		}
	}, [clientId, statutoryHolidayFormDataByClientId]);



	const { isPending: isDeleting, mutate: deleteStatutoryHolidayById } =
		useMutation({
			mutationFn: CompanyStatutoryHolidayService.delStatutoryHoliday,

			onSuccess: (res: string | IServerError | IResponse) => {
				if (typeof res === "string") {
					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
				} else {
					setSnackbarData({
						open: true,
						message: "Something went wrong, try again later!",
						severity: "error",
					});
				}
				formMethods.reset();
				scrollPanelFieldsToTop();
				let path = `/settings/statutory-holiday`;
				navigate(path);
				return queryClient.invalidateQueries({
					queryKey: [STATORY_HOLIDAY_RECORD_CARDS_DATA_QUERY_KEY],
				});
			},
			onError: () => {
				// TODO: customize the toast message based on the response
				setSnackbarData({
					open: true,
					message: "Something went wrong, try again later!",
					severity: "error",
				});
			},
		});

	const deleteStatutoryHolidayField = () => {
		if (singleDelete) {
			setSingleDelete(false);
			let delPayload = {
				statutoryHolidayId: statutoryHolidayId,
				navigateToLogout: navigateToLogout,
			};
			deleteStatutoryHolidayById(delPayload);
			setDialogOpen(false);
		}
	};

	const onDeleteClicked = () => {
		setDialogOpen(true);
		setSingleDelete(true);
		if (statutoryHolidayFormDataByClientId) {
			setStatutoryHolidayId(statutoryHolidayFormDataByClientId.id);
		}
	};

	const onRowDeleteClicked = useCallback((params: any) => {

		setDialogOpen(true);
		setSingleDelete(true);
		setStatutoryHolidayId(params.id);
	}, []);

	const { isPending: isArchive, mutate: archiveStatutoryHolidayById } =
		useMutation({
			mutationFn: CompanyStatutoryHolidayService.archiveStatutoryHoliday,

			onSuccess: (res: string | IServerError | IResponse) => {
				if (typeof res === "string") {
					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
				} else {
					setSnackbarData({
						open: true,
						message: "Something went wrong, try again later!",
						severity: "error",
					});
				}
				formMethods.reset();
				let path = `/settings/statutory-holiday`;
				navigate(path);
				return queryClient.invalidateQueries({
					queryKey: [STATORY_HOLIDAY_RECORD_CARDS_DATA_QUERY_KEY],
				});
			},
			onError: () => {
				// TODO: customize the toast message based on the response
				setSnackbarData({
					open: true,
					message: "Something went wrong, try again later!",
					severity: "error",
				});
			},
		});

	const { isPending: isPendingDrag, mutate: submitFormData } = useMutation({
		mutationFn: CompanyStatutoryHolidayService.createStatutoryHolidayEmployeeEligibilityPanel,
		onSuccess: (res: IServerError | IResponse | null | any) => {
			if (res?.status === true) {
				setSnackbarData({
					open: true,
					message:
						"Employee(s) has been assigned to the Eligibility panel successfully",
					severity: "success",
				});
			} else {
				setSnackbarData({
					open: true,
					message:
						"Employee(s) has been unassigned from the Eligibility panel successfully",
					severity: "warning",
				});
			}
			refetchAssignedEligiblePanels();
		},
		onError: (err) => {
			setSnackbarData({
				open: true,
				message: "An error occurred, please try again",
				severity: "error",
			});
		},
	});

	const { isPending: isPendingBulkDrag, mutate: submitBulkFormData } = useMutation({
		mutationFn: CompanyStatutoryHolidayService.createStatutoryHolidayBulkEmployeeEligibilityPanel,
		onSuccess: (res: IServerError | IResponse | null | any) => {
			if (res?.status === true) {
				setSnackbarData({
					open: true,
					message:
						"Employee(s) has been assigned to the Eligibility panel successfully",
					severity: "success",
				});
			} else {
				setSnackbarData({
					open: true,
					message:
						"Employee(s) has been unassigned from the Eligibility panel successfully",
					severity: "warning",
				});
			}
			refetchAssignedEligiblePanels();
		},
		onError: (err) => {
			setSnackbarData({
				open: true,
				message: "An error occurred, please try again",
				severity: "error",
			});
		},
	});

	useEffect(() => {
		if (
			assignedEligiblePanelResponsePayload &&
			instanceOfICompanyStatutoryHolidayScreenPanelAssignmentResponsePayload(
				assignedEligiblePanelResponsePayload
			)
		) {
			let eligibleEmployees: IDNDList = {
				droppableId: firstPanelDraggableId,
				elements: sortDragDropContextDDLByText(assignedEligiblePanelResponsePayload.eligibleEmployees).map(
					(eligibleEmployeesFlexField: any) => ({
						id: String(eligibleEmployeesFlexField.id),
						text: String(eligibleEmployeesFlexField.text),
						value: String(eligibleEmployeesFlexField.value),
					})
				),
				testId: firstPanelDraggableId,
			};

			let ineligibleEmployees: IDNDList = {
				droppableId: secondPanelDraggableId,
				elements: sortDragDropContextDDLByText(assignedEligiblePanelResponsePayload.ineligibleEmployees).map(
					(ineligibleEmployeesFlexField: any) => ({
						id: String(ineligibleEmployeesFlexField.id),
						text: String(ineligibleEmployeesFlexField.text),
						value: String(ineligibleEmployeesFlexField.value),
					})
				),
				testId: secondPanelDraggableId,
			};

			setFirstList(eligibleEmployees);
			setSecondList(ineligibleEmployees);
		}



	}, [assignedEligiblePanelResponsePayload]);

	const archiveStatutoryHolidayField = () => {
		if (singleArchive) {
			setSingleArchive(false);
			let ArchivePayload = {
				statutoryHolidayId: statutoryHolidayId,
				navigateToLogout: navigateToLogout,
			};
			archiveStatutoryHolidayById(ArchivePayload);
			setArchiveDialogOpen(false);
		}
	};

	const onArchiveClicked = () => {
		setArchiveDialogOpen(true);
		setSingleArchive(true);
		if (statutoryHolidayFormDataByClientId) {
			setStatutoryHolidayId(statutoryHolidayFormDataByClientId.clientId);
		}
	};

	const onCloseHandler = () => {
		setDialogOpen(false);
	};
	const onCloseArchiveHandler = () => {
		setArchiveDialogOpen(false);
	};

	const onOpenEligibilityPanel = () => {
		setOpenEligibilityPanel(true);
		refetchAssignedEligiblePanels();

	};
	const onCloseEligibilityPanel = () => {
		setOpenEligibilityPanel(false);
	};

	const expandedLabels = ["Statutory Holiday"];
	const expandedStatutoryHolidaySetupOrSetupItem = "Statutory Holiday";
	const enhanceData = (data: any[], parentLabel?: string): any[] => {
		return data.map((item) => {
			let isExpanded = expandedLabels.includes(item.label);
			if (
				parentLabel === "Statutory Holiday" &&
				item.label === expandedStatutoryHolidaySetupOrSetupItem
			) {
				isExpanded = true;
			}
			const enhancedItem = {
				...item,
				expanded: item.items ? isExpanded : false,
				items: item.items ? enhanceData(item.items, item.label) : undefined,
				path: item.label === expandedStatutoryHolidaySetupOrSetupItem && "/settings/statutory-holiday",
			};
			return enhancedItem;
		});
	};

	useEffect(() => {
		dispatch(setSelectedStickyNavBar({ isSticky: true }));
		if (drawerMenuOptions && drawerMenuOptions.length > 0) {
			const enhancedData = enhanceData(drawerMenuOptions);
			dispatch(setSelectedDrawerOptionsMenu({ menu: enhancedData }));
		}
	}, [drawerMenuOptions]);

	const onRowEditClicked = useCallback(
		(params: ICompanyStatutoryHoliday | any) => {
			// alert(`Edit row with ID: ${params.row.id} has been clicked!`);
			let path = `settings/statutory-holiday/${params.value}`;
			navigate(path);
		},
		[]
	);

	const onSubmitPayCalculationsRulesHandler = useCallback(
		(shortCode: string, label: string) => {
			const formValues = formMethods.getValues();


			const provinceSelected = provinceCardsData[shortCode];
			if (provinceSelected && provinceSelected) {
				for (const rule of provinceSelected.rules) {
					if (rule.valueField) {
						const ruleFieldValue = formValues[rule.valueField];

						if (
							!ruleFieldValue ||
							ruleFieldValue === "" ||
							ruleFieldValue === "regular" ||
							ruleFieldValue === "regularContinuous"
						) {
							let errorMessage = "";

							if (shortCode === "MB" || shortCode === "SK") {
								errorMessage = "Please select formula for holidays not worked.";
							} else {
								errorMessage =
									"Please select formula for regular employees for holidays worked.";
							}

							setSnackbarData({
								open: true,
								message: errorMessage,
								severity: "error",
							});
							return;
						}

					}
				}
				const province: any = provinceDDL && provinceDDL?.find((x: any) => x.label === label)
				const payload = {
					clientId: clientId,
					payCalculationRules: provinceSelected.rules.map(
						(rule: { ruleType: any; valueField: string; value: any }) => ({
							effectiveDate: formValues[provinceSelected.dateField],
							province: {
								id: province.value,
								name: province.label,
								shortCode: province.shortCode,
							},
							ruleType: rule.ruleType,
							ruleTypeValue: rule.valueField
								? formValues[rule.valueField]
								: rule.value,
						})
					)
				}

				submitCompanyStatutoryHolidayProvinceCardFields(payload);


			}


			// setSnackbarData({
			// 	open: true,
			// 	message: "Form submit successfully",
			// 	severity: "success",
			// });
		},
		[provinceDDL]
	);

	const onResetCardButtonClicked = useCallback(
		(shortCode: string, label: string) => {
			setSnackbarData({
				open: true,
				message: "Success",
				severity: "success",
			});
			scrollPanelFieldsToTop();
			const province = provinceMap[shortCode as keyof typeof provinceMap];
			if (province && province.label === label) {
				formMethods.setValue(province.dateField, "2024-12-25");
				province.fields.forEach((field) => formMethods.setValue(field, null));
			}
		},
		[formMethods]
	);

	const onListsChange = useCallback(
		(updatedFirstList: IDNDList, updatedSecondList: IDNDList) => {
			setFirstList(updatedFirstList);
			setSecondList(updatedSecondList);
		},
		[]
	);
	const onMoveItems = useCallback(
		(payload: IMoveItemsPayload) => {
			// moving into secondList => flag false (ineligible), into firstList => flag true (eligible)
			const flag = payload.destinationDroppableId === secondPanelDraggableId;

			if (payload.isBulk) {
				submitBulkFormData({
					ids: payload.movedItemIds,
					flag: !flag,
				});
			} else {
				submitFormData({
					employeeId: payload.movedItemIds[0],
					flag: !flag,
				});
			}
		},
		[submitFormData, submitBulkFormData]
	);


	// const getListById = useCallback(
	// 	(droppableId: string) =>
	// 		droppableId === firstList.droppableId ? firstList : secondList,
	// 	[firstList, secondList]
	// );
	// const onItemClick = useCallback(
	// 	(e: React.MouseEvent, droppableId: string, itemId: string, index: number) => {
	// 		const isRangeKey = e.shiftKey;

	// 		// clicking in a different list starts a fresh selection there
	// 		if (selectedListId && selectedListId !== droppableId) {
	// 			setSelectedIds([itemId]);
	// 			setSelectedListId(droppableId);
	// 			lastSelectedIndexRef.current = index;
	// 			return;
	// 		}

	// 		if (isRangeKey && lastSelectedIndexRef.current !== null) {
	// 			const list = getListById(droppableId);
	// 			const start = Math.min(lastSelectedIndexRef.current, index);
	// 			const end = Math.max(lastSelectedIndexRef.current, index);
	// 			const rangeIds = list.elements
	// 				.slice(start, end + 1)
	// 				.map((el: any) => el.id);
	// 			setSelectedIds((prev) => Array.from(new Set([...prev, ...rangeIds])));
	// 			setSelectedListId(droppableId);
	// 			return;
	// 		}

	// 		// plain click now TOGGLES the item in/out of the selection
	// 		setSelectedIds((prev) =>
	// 			prev.includes(itemId)
	// 				? prev.filter((id) => id !== itemId)
	// 				: [...prev, itemId]
	// 		);
	// 		setSelectedListId(droppableId);
	// 		lastSelectedIndexRef.current = index;
	// 	},
	// 	[selectedListId, getListById]
	// );
	// const onDragStart = useCallback(
	// 	(start: any) => {
	// 		const { draggableId, source } = start;
	// 		if (!selectedIds.includes(draggableId)) {
	// 			setSelectedIds([draggableId]);
	// 			setSelectedListId(source.droppableId);
	// 		}
	// 	},
	// 	[selectedIds]
	// );
	// const onDragEnd = useCallback(
	// 	(result: any) => {
	// 		const { source, destination, draggableId } = result;
	// 		if (!destination) return;

	// 		const sameList = source.droppableId === destination.droppableId;
	// 		if (sameList && source.index === destination.index) return;

	// 		const isMulti = selectedIds.includes(draggableId) && selectedIds.length > 1;
	// 		const idsToMove = isMulti ? selectedIds : [draggableId];

	// 		if (sameList) {
	// 			// --- Reorder within the same list — NO API call ---
	// 			const list = getListById(source.droppableId);
	// 			const remaining = list.elements.filter(
	// 				(el: any) => !idsToMove.includes(el.id)
	// 			);
	// 			const movedItems = list.elements.filter((el: any) =>
	// 				idsToMove.includes(el.id)
	// 			);
	// 			const insertAt = Math.min(destination.index, remaining.length);
	// 			const updatedElements = [
	// 				...remaining.slice(0, insertAt),
	// 				...movedItems,
	// 				...remaining.slice(insertAt),
	// 			];

	// 			if (list.droppableId === firstList.droppableId) {
	// 				setFirstList({ ...list, elements: updatedElements });
	// 			} else {
	// 				setSecondList({ ...list, elements: updatedElements });
	// 			}
	// 		} else {
	// 			// --- Cross-list move — API call happens ONLY here ---
	// 			const sourceList = getListById(source.droppableId);
	// 			const destinationList = getListById(destination.droppableId);

	// 			const movedItems = sourceList.elements.filter((el: any) =>
	// 				idsToMove.includes(el.id)
	// 			);
	// 			const updatedSourceElements = sourceList.elements.filter(
	// 				(el: any) => !idsToMove.includes(el.id)
	// 			);
	// 			const updatedDestinationElements = [...destinationList.elements];
	// 			updatedDestinationElements.splice(destination.index, 0, ...movedItems);

	// 			if (sourceList.droppableId === firstList.droppableId) {
	// 				setFirstList({ ...sourceList, elements: updatedSourceElements });
	// 				setSecondList({ ...destinationList, elements: updatedDestinationElements });
	// 			} else {
	// 				setSecondList({ ...sourceList, elements: updatedSourceElements });
	// 				setFirstList({ ...destinationList, elements: updatedDestinationElements });
	// 			}

	// 			// moving into secondList => flag false (ineligible), into firstList => flag true (eligible)
	// 			const flag = destination.droppableId === secondPanelDraggableId;
	// 			console.log('movedItems', movedItems)
	// 			if (movedItems.length > 1) {
	// 				const selectedItemIds = movedItems.map((x: any) => x.id)
	// 				console.log('selectedItemIds', selectedItemIds)
	// 				let Payload = {
	// 					ids: selectedItemIds,
	// 					flag: !flag,
	// 				};
	// 				submitBulkFormData(Payload);

	// 			} else {
	// 				let Payload = {
	// 					employeeId: movedItems[0].id,
	// 					flag: !flag,
	// 				};
	// 				submitFormData(Payload);

	// 				// If your API supports batch updates, prefer this single call instead of the loop above:
	// 				// submitFormData({ employeeIds: movedItems.map((i: any) => i.id), flag: !flag });
	// 			}
	// 		}

	// 		setSelectedIds([]);
	// 		setSelectedListId(null);
	// 		lastSelectedIndexRef.current = null;
	// 	},
	// 	[firstList, secondList, selectedIds, getListById, submitFormData]
	// );

	// const onDragEnd = (result: any, _provided: any) => {
	// 	if (
	// 		result.destination &&
	// 		result.source.droppableId !== result.destination.droppableId
	// 	) {
	// 		if (result.source.droppableId === "firstList") {
	// 			let sourceList = firstList;
	// 			let destinationList = secondList;
	// 			let movedItem = sourceList.elements.splice(result.source.index, 1)[0];
	// 			destinationList.elements.splice(result.destination.index, 0, movedItem);
	// 			setFirstList({
	// 				...sourceList,
	// 			});
	// 			setSecondList({
	// 				...destinationList,
	// 			});
	// 		} else {
	// 			let sourceList = secondList;
	// 			let destinationList = firstList;
	// 			let movedItem = sourceList.elements.splice(result.source.index, 1)[0];
	// 			destinationList.elements.splice(result.destination.index, 0, movedItem);
	// 			setSecondList({
	// 				...sourceList,
	// 			});
	// 			setFirstList({
	// 				...destinationList,
	// 			});
	// 		}
	// 	} else {
	// 		if (!result.destination) return;
	// 		if (result.source.droppableId === "firstList") {
	// 			let sourceDestinationList = firstList;
	// 			let sourceItem = sourceDestinationList.elements.splice(
	// 				result.source.index,
	// 				1
	// 			)[0];
	// 			sourceDestinationList.elements.splice(
	// 				result.destination.index,
	// 				0,
	// 				sourceItem
	// 			);
	// 			setFirstList({
	// 				...sourceDestinationList,
	// 			});
	// 		} else {
	// 			let sourceDestinationList = secondList;
	// 			let sourceItem = sourceDestinationList.elements.splice(
	// 				result.source.index,
	// 				1
	// 			)[0];
	// 			sourceDestinationList.elements.splice(
	// 				result.destination.index,
	// 				0,
	// 				sourceItem
	// 			);
	// 			setSecondList({
	// 				...sourceDestinationList,
	// 			});
	// 		}
	// 	}

	// 	if (result.destination.droppableId === "secondList") {
	// 		let Payload = {
	// 			employeeId: result.draggableId,
	// 			flag: !true,
	// 		};
	// 		submitFormData(Payload);
	// 	} else {
	// 		let Payload = {
	// 			employeeId: result.draggableId,
	// 			flag: !false,
	// 		};
	// 		submitFormData(Payload);
	// 	}
	// };

	const { isLoading: isPendingStatutoryHolidayProfileListStatus, data } = useGetStatutoryHolidayProfileListStatus(
		clientId,
		taxYear,
		navigateToLogout
	);
	const statutoryHolidayProfileListStatusData: any | null = data;
	console.log("statutoryHolidayProfileListStatusItems", statutoryHolidayProfileListStatusData)

	const { isPending: isUpdatingStatutoryHolidayProfileListStatusData, mutate: updateStatutoryHolidayProfileListStatusData, } = useMutation({
		mutationFn: CompanyStatutoryHolidayService.updateStatutoryHolidayProfileListStatus,
		onSuccess: (
			res: ICompanyStatutoryHolidayForm | IServerError | IResponse | any
		) => {

			if (res.status === 200) {
				setSnackbarData({
					open: true,
					message: "Success",
					severity: "success",
				});
				scrollPanelFieldsToTop();
				let path = `/settings/statutory-holiday/`;
				navigate(path);
				return queryClient.invalidateQueries({ queryKey: [STATUTORY_HOLIDAY_PROFILE_LIST_STATUS] });
			} else if (res.status === 201) {
				setSnackbarData({
					open: true,
					message: "Success",
					severity: "success",
				});
				scrollPanelFieldsToTop();
				let path = `/settings/statutory-holiday/`;
				navigate(path);
				return queryClient.invalidateQueries({ queryKey: [STATUTORY_HOLIDAY_PROFILE_LIST_STATUS] });
			} else {
				setSnackbarData({
					open: true,
					message: res.message ? res.message : "Something went wrong!",
					severity: "error",
				});
			}
		},
		onError: () => {
			setSnackbarData({
				open: true,
				message: "Something went wrong, try again later!",
				severity: "error",
			});
		},
	});

	const onStatusChangeClickedHandler = (rowData: any) => {
		const payload = {
			...rowData,
			holidayProvince: rowData.provinceShortCode,
			provinceShortCode: undefined,
			clientId: clientId,
		};
		console.log("payload", payload)
		updateStatutoryHolidayProfileListStatusData(payload)
	}
	let statutoryHolidayDataItems = {
		items: [
			{

				status: 200,
				provinceShortCode: "BC",
				holidayDate: "Aug 24, 2026",

			},
			{
				status: 100,
				provinceShortCode: "BC",
				holidayDate: "Aug 24, 2026",

			},
			{
				status: 200,
				provinceShortCode: "BC",
				holidayDate: "Aug 24, 2026",

			},
			{
				status: 200,
				provinceShortCode: "BC",
				holidayDate: "Aug 24, 2026",
			},
			{
				status: 200,
				provinceShortCode: "BC",
				holidayDate: "Aug 24, 2026",

			},
		],
	}

	return (
		<Grid
			sx={{
				pb: "5rem",
				"& .MuiAppBar-root": {
					// zIndex: 2,
					top: subMenuVisibility ? "154px" : "112px",
				},
			}}
		>
			<BackdropCircularProgress
				open={isSubmitting || isPendingStatutoryHolidayFormDataByClientId || isDeleting || isArchive || submitCompanyStatutoryHolidayProvinceCardField || isLoadingStatutoryHolidayDDL || isPendingStatutoryHolidayProfileListStatus || isUpdatingStatutoryHolidayProfileListStatusData}
				testId="company statutory holiday setup"
			/>
			<DeleteConfirmationDialog
				open={openDialog}
				deleteActionHandler={deleteStatutoryHolidayField}
				cancelActionHandler={onCloseHandler}
				testId="Company-statutory-holiday-confirmation-dialog"

			/>
			<FormProvider {...formMethods}>
				<form onSubmit={formMethods.handleSubmit(onSubmit)}>
					<Grid container size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
						<Grid size={{ xs: 12, sm: 12, md: 3.5, lg: 3.5, xl: 3.5 }}>
							<SingleColumnLayout
								children={
									<StandardPanel
										title="Statutory Holiday"
										testId={`companystatutoryHoliday`}
										// leftSpacing={1}
										// rightSpacing={1}
										showTitle={false}
										variant="outlined"
										topPadding={2}
									>
										<CompanyStatutoryHolidayInfoSetupFieldsPanel
											provinceDDLItems={(value: any) =>
												setProvinceDDLItems(value)
											}
											onDisableCardsOptions={(value: boolean) =>
												setDisableCards(value)
											}
											handleOpenEligibilityPanelDragable={
												onOpenEligibilityPanel
											}
											statutoryHolidayFormDataByClientId={statutoryHolidayFormDataByClientId}
											onSnackbarMessageData={(value: any) => setSnackbarData(value)}
										/>
									</StandardPanel>
								}
								toolbar={<CompanyStatutoryHolidaySetupToolbar

									// onDeleteClicked={onDeleteClicked}
									// isSubmitting={false}
									id={id}
								/>}
								showFullDiaplay={true}
								toolbarPosition="bottom"
								testId="form-layout"
							/>

							{/* Card Grid */}

							<StatutoryHolidaysProvinceCards
								onSubmitPayCalculationsRules={onSubmitPayCalculationsRulesHandler}
								onResetCardButtonClicked={onResetCardButtonClicked}
								cardDisableOption={disableCards}
								statutoryHolidayDDL={statutoryHolidayDDLForAllProvinces && statutoryHolidayDDLForAllProvinces}
								setSelectedProvinceId={(id: any) => setProvinceId(id)}
								setSelectedProvinceSC={(sc: any) => setProvinceSC(sc)}
								provinceDDL={provinceDDL && provinceDDL}
								clientProvince={statutoryHolidayFormDataByClientId && statutoryHolidayFormDataByClientId?.clientProvince}
							/>
						</Grid>

						<Grid size={{ xs: 0, sm: 0, md: 2.5, lg: 2.5, xl: 2.5 }}></Grid>
						<Grid
							size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}
							sx={{ marginTop: "1rem" }}
						>
							<DDLDataTableStatutoryHoliday
								rows={
									getProvinceCardsList ?? []
								}

								columns={[
									{ field: "effectiveDate", headerName: "Date Effective" },
									{ field: "ruleType", headerName: "Employment" },
									{ field: "province", headerName: "Province" },
									{ field: "ruleTypeValue", headerName: "Method" },
								]}
								showHeaderColumns={true}
								onRowEditClicked={onRowEditClicked}
								onRowDeleteClicked={onRowDeleteClicked}
								onRowArchiveClicked={onArchiveClicked}
								// isLoading={isPending}
								totalRecords={
									getProvinceCardsList && getProvinceCardsList.total
										? getProvinceCardsList.total
										: 0
								}
								initialPageSize={INITIAL_PAGE_SIZE}
								rowsPerPageOptions={ROWS_PER_PAGE_PER_OPTIONS}
								onRequestParamsChange={onRequestParamsChange}
								tableScrollHeight={"60vh"}
								showPaginator={false}
							/>
							<br />
							<br />
							<StatutoryHolidayProfileListStatusDataTable
								statutoryHolidayProfileListUpdatedData={(data: any) =>
									setStatutoryHolidaysProfileListStatusUpdatedData(data)
								}
								statutoryHolidayProfileListStatusHeader={"Statutory Holiday List"}
								// statutoryHolidayProfileListDataItems={statutoryHolidayProfileListStatusData.items} // Api Data Used 
								statutoryHolidayProfileListDataItems={statutoryHolidayDataItems.items}
								onStatusChangeClickedHandler={onStatusChangeClickedHandler}
							/>

						</Grid>


						<Grid size={{ xs: 0, sm: 0, md: 2.5, lg: 2.5, xl: 2.5 }}></Grid>
						<Grid
							size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}
							sx={{ marginTop: "16px" }}
						></Grid>

						<Grid size={{ xs: 0, sm: 0, md: 0, lg: 1, xl: 1 }}></Grid>
					</Grid>
				</form>
			</FormProvider>

			{/* Archive Dialogue */}

			<EnhancedDialog
				dialogTitle="Please Confirm"
				key="dialog-box"
				testId="dialog"
				onCloseHandler={onArchiveClicked}
				open={openArchiveDialog}
				dialogContent={null}
				showdividers={false}
				dialogAction={
					<Grid>
						<EnhancedButtonPrimeReact
							label="Cancel"
							severity="secondary"
							text
							raised
							onClickHandler={onCloseArchiveHandler}
							style={{ fontSize: "small" }}
						/>
						<EnhancedButtonPrimeReact
							label="Archive"
							severity="secondary"
							raised
							onClickHandler={() => archiveStatutoryHolidayField}
							style={{ marginLeft: "16px", fontSize: "small" }}
						/>
					</Grid>
				}
			/>

			<EnhancedDialog
				dialogTitle="Manage Statutory Holiday Eligibility"
				key="Manage Statutory Holiday Eligibility"
				testId="Manage Statutory Holiday Eligibility"
				onCloseHandler={onCloseEligibilityPanel}
				open={openEligibilityPanel}
				dialogContent={
					// <StatutoryHolidayEligibilityPanel
					// 	firstList={firstList}
					// 	secondList={secondList}
					// 	onDragEnd={onDragEnd}
					// 	onDragStart={onDragStart}
					// 	selectedIds={selectedIds}
					// 	onItemClick={onItemClick}
					// 	isLoading={isLoadingAssignedClients || isPendingDrag || isPendingBulkDrag}
					// />

					<StatutoryHolidayEligibilityPanel
						firstList={firstList}
						secondList={secondList}
						onListsChange={onListsChange}
						onMoveItems={onMoveItems}
						isLoading={isLoadingAssignedClients || isPendingDrag || isPendingBulkDrag}
					/>
				}
				showdividers={false}
				dialogAction={null}
			/>

			<EnhancedSnackbar
				message={snackbarData.message}
				onCloseHandler={onSnackbarClosed}
				severity={snackbarData.severity}
				testId={"company-statutory-holiday-snackbar"}
				open={snackbarData.open}
			/>
		</Grid>
	);
};
